import {
    Box3,
    Vector3,
    Mesh,
    BoxGeometry,
    MeshBasicMaterial
} from 'three';

import WebGLGroup from '../_app/cuchillo/3D/WebGLGroup';
import ModelGLB from '../_app/cuchillo/3D/ModelGLB';
import GeometryController from '../_app/cuchillo/3D/GeometryController';

import { Basics, isTouch } from '../_app/cuchillo/core/Basics';
import { Interaction } from '../_app/cuchillo/core/Interaction';

// Valors base de flotació. Es randomitzen lleugerament per tapon (amb seed) a cada resize().
const FLOAT_BASE = {
    speedX: 0.01,
    speedY: 0.007,
    speedZ: 0.005,
    speedPY: 0.02,

    ampX: 0.08,
    ampY: 0.14,
    ampZ: 0.05,
    ampPY: 11,

    rotFactor: 0.07,
    zFactor: 0.1,
};

export const SPIN_SETTINGS = {
    intensity: 0.001,
    friction: 0.961,
};

// PRNG determinista a partir d'un seed (mulberry32)
function mulberry32(a) {
    return function () {
        a |= 0;
        a = a + 0x6D2B79F5 | 0;

        let t = Math.imul(a ^ a >>> 15, 1 | a);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;

        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
}

// Objecte tapon independent (tapon-single.glb) mapejat a un element del DOM,
// amb material propi, flotació i drag.
export default class Tapon extends ModelGLB {
    tick = 0;

    spinGroup = new WebGLGroup({ rotFactor: 0.08 });
    floatGroup = new WebGLGroup({
        rotFactor: FLOAT_BASE.rotFactor,
        zFactor: FLOAT_BASE.zFactor
    });

    _spinVelY = 0;
    _spinAccumY = 0;

    _isMouseDown = false;
    _prevMouseX = 0;
    _prevMouseY = 0;

    constructor(opts = {}) {
        super(opts);

        this.hasFlotation = opts.hasFlotation ?? true;
        this.hasDrag = opts.hasDrag ?? false;

        this._seed = opts.seed ?? (Math.random() * 10000);
        this._float = null;

        this.spinGroup.add(this.floatGroup);
        this.add(this.spinGroup);

        if (this.hasDrag) {
            this._onDown = this._handleDown.bind(this);
            this._onUp = this._handleUp.bind(this);
            this._onMove = this._handleMove.bind(this);

            document.addEventListener(Basics.downEvent, this._onDown);
            document.addEventListener(Basics.upEvent, this._onUp);
            document.addEventListener(Basics.moveEvent, this._onMove);
        }
    }

    _handleDown(e) {
        const mx = isTouch
            ? e.touches[0].clientX
            : e.clientX;

        const my = isTouch
            ? e.touches[0].clientY
            : e.clientY;

        const { x, y, width, height } =
            this.dom.getBoundingClientRect();

        if (
            mx >= x &&
            mx <= x + width &&
            my >= y &&
            my <= y + height
        ) {
            this._isMouseDown = true;

            this._prevMouseX = Interaction.positions.mouse.x;
            this._prevMouseY = Interaction.positions.mouse.y;

            document.body.classList.add('--tapon-dragging');
        }
    }

    _handleUp() {
        this._isMouseDown = false;
        document.body.classList.remove('--tapon-dragging');
    }

    _handleMove() {
        if (!this._isMouseDown) return;

        const mx = Interaction.positions.mouse.x;
        const my = Interaction.positions.mouse.y;

        this._spinVelY +=
            (mx - this._prevMouseX) *
            SPIN_SETTINGS.intensity;

        this._prevMouseX = mx;
        this._prevMouseY = my;
    }

    init() {
        return new Promise((resolve, reject) => {
            GeometryController.load({
                src: this.model,

                call: (gltf) => {
                    this.object = gltf;
                    this.mesh = gltf.scene.children[0];

                    this.floatGroup.add(gltf.scene);

                    if (this._material) {
                        gltf.scene.traverse(o => {
                            if (o.isMesh) {
                                o.material = this._material;
                                o.material.needsUpdate = true;
                            }
                        });
                    }

                    // Proxy invisible per al raycaster
                    this.updateMatrixWorld(true);

                    const bb = new Box3().setFromObject(this.mesh);

                    bb.applyMatrix4(
                        this.mesh.matrixWorld.clone().invert()
                    );

                    const sz = bb.getSize(new Vector3());
                    const ctr = bb.getCenter(new Vector3());

                    const proxy = new Mesh(
                        new BoxGeometry(
                            Math.max(sz.x, 1e-3),
                            Math.max(sz.y, 1e-3),
                            Math.max(sz.z, 1e-3)
                        ),
                        new MeshBasicMaterial()
                    );

                    proxy.visible = false;
                    proxy.position.copy(ctr);

                    this.mesh.add(proxy);
                    this._proxy = proxy;

                    this.size.copy(this.mesh.scale);

                    this.resize();

                    resolve();
                }
            }, undefined, reject);
        });
    }

    setOpacity(value) {
        if (!this._material) return;

        this._material.transparent = true;
        this._material.opacity = value;
    }

    // Randomitza l'estat de flotació
    _randomizeFloat() {
        const rnd =
            mulberry32(Math.floor(this._seed) >>> 0);

        const TWO_PI = Math.PI * 2;

        const v = (base, spread = 0.4) =>
            base * (
                1 -
                spread * 0.5 +
                rnd() * spread
            );

        this._float = {
            phaseX: rnd() * TWO_PI,
            phaseY: rnd() * TWO_PI,
            phaseZ: rnd() * TWO_PI,
            phasePY: rnd() * TWO_PI,

            speedX: v(FLOAT_BASE.speedX),
            speedY: v(FLOAT_BASE.speedY),
            speedZ: v(FLOAT_BASE.speedZ),
            speedPY: v(FLOAT_BASE.speedPY),

            ampX: v(FLOAT_BASE.ampX),
            ampY: v(FLOAT_BASE.ampY),
            ampZ: v(FLOAT_BASE.ampZ),
            ampPY: v(FLOAT_BASE.ampPY),
        };

        const lf = 0.95 + rnd() * 0.1;

        this.floatGroup.opts.rotFactor =
            FLOAT_BASE.rotFactor * lf;

        this.floatGroup.opts.zFactor =
            FLOAT_BASE.zFactor * lf;
    }

    update() {
        this.tick++;

        // DRAG

        this._spinVelY *= SPIN_SETTINGS.friction;
        this._spinAccumY += this._spinVelY;

        this.spinGroup.rot.y = this._spinAccumY;
        this.spinGroup.update();

        // FLOTACIÓ

        if (this.hasFlotation && this._float) {
            const f = this._float;

            this.floatGroup.rot.x =
                Math.sin(
                    this.tick * f.speedX +
                    f.phaseX
                ) * f.ampX;

            this.floatGroup.rot.y =
                Math.sin(
                    this.tick * f.speedY +
                    f.phaseY
                ) * f.ampY;

            this.floatGroup.rot.z =
                Math.sin(
                    this.tick * f.speedZ +
                    f.phaseZ
                ) * f.ampZ;

            this.floatGroup.pos.y =
                Math.sin(
                    this.tick * f.speedPY +
                    f.phasePY
                ) * f.ampPY;
        }

        this.floatGroup.update();

        super.update();
    }

    resize(...args) {
        super.resize(...args);
        this._randomizeFloat();
    }

    dispose() {
        if (this.hasDrag) {
            document.removeEventListener(
                Basics.downEvent,
                this._onDown
            );

            document.removeEventListener(
                Basics.upEvent,
                this._onUp
            );

            document.removeEventListener(
                Basics.moveEvent,
                this._onMove
            );
        }

        if (this.object) {
            this.floatGroup.remove(this.object.scene);
        }

        this.spinGroup.remove(this.floatGroup);

        this.remove(this.spinGroup);

        super.dispose();
    }
}